import { CompanyAddress, PartnerRepresentative, PartnerCustomsInfo } from "@assets/types";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import CompanyService from "@services/CompanyService";
import CreateBookingService from "@services/CreateBookingService";
import { RootState } from "@state/store";

class PartnerFilter {
  partnerCode: string;
  addressType: string;
}

export const getPartnerDefaultAddresses = createAsyncThunk(
  "partner/default-addresses",
  async (partnerFilter: PartnerFilter) => {
    return await CompanyService.getPartnerDefaultAddresses(
      partnerFilter.partnerCode
    );
  }
);

export const getPartnerAddress = createAsyncThunk(
  "partner/partneraddress",
  async (partnerFilter: PartnerFilter) => {
    return await CreateBookingService.getPartnerAddress(
      partnerFilter.partnerCode,
      partnerFilter.addressType
    );
  }
);

export const getCompanyCustomerAddresses = createAsyncThunk(
  "partner/customers",
  async (partnerFilter: PartnerFilter) => {
    return await CreateBookingService.getCompanyCustomerAddresses(
      partnerFilter.partnerCode,
      partnerFilter.addressType
    );
  }
);

export const getCompanyAddresses = createAsyncThunk(
  "partner/addresses",
  async (partnerFilter: PartnerFilter) => {
    return await CreateBookingService.getCompanyAddresses(
      partnerFilter.partnerCode,
      partnerFilter.addressType
    );
  }
);

export const searchPartners = createAsyncThunk(
  "partner/search/customers",
  async (searchString: string) => {
    return await CreateBookingService.searchPartners(searchString);
  }
);

export const getPartnerRepresentatives = createAsyncThunk(
  "partner/representatives",
  async (partnerCode: string) => {
    return await CompanyService.getPartnerRepresentatives(partnerCode);
  }
);

export const getPartnerCustomsInfo = createAsyncThunk(
  "partner/customs-info",
  async (partnerCode: string) => {
    return await CompanyService.getPartnerCustomsInfo(partnerCode);
  }
);

export interface PartnerState {
  partnerAddress: CompanyAddress | null;
  loadingPartnerAddresses: boolean;
  defaultAddresses: CompanyAddress[];
  loadingDefaultAddresses: boolean;
  companyCustomerAddresses: CompanyAddress[];
  loadingCompanyAddresses: boolean;
  companyAddresses: CompanyAddress[];
  loadingCompanyCustomerAddresses: boolean;
  searchPartners: CompanyAddress[];
  representatives: PartnerRepresentative[];
  loadingRepresentatives: boolean;
  customsInfo: PartnerCustomsInfo | null;
  loadingCustomsInfo: boolean;
}

export const initialPartnerState: PartnerState = {
  partnerAddress: null,
  loadingPartnerAddresses: false,
  defaultAddresses: [],
  loadingDefaultAddresses: false,
  companyCustomerAddresses: [],
  loadingCompanyAddresses: false,
  companyAddresses: [],
  loadingCompanyCustomerAddresses: false,
  searchPartners: [],
  representatives: [],
  loadingRepresentatives: false,
  customsInfo: null,
  loadingCustomsInfo: false,
};

export const partnerSlice = createSlice({
  name: "partnerAddress",
  initialState: initialPartnerState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Default Addresses
      .addCase(getPartnerDefaultAddresses.pending, (state) => {
        state.loadingDefaultAddresses = true;
      })
      .addCase(getPartnerDefaultAddresses.rejected, (state) => {
        state.loadingDefaultAddresses = false;
        state.defaultAddresses = [];
      })
      .addCase(
        getPartnerDefaultAddresses.fulfilled,
        (state, action: PayloadAction<CompanyAddress[]>) => {
          state.loadingDefaultAddresses = false;
          state.defaultAddresses = action.payload;
        }
      )
      // Partner Address
      .addCase(getPartnerAddress.pending, (state) => {
        state.loadingPartnerAddresses = true;
      })
      .addCase(getPartnerAddress.rejected, (state) => {
        state.loadingPartnerAddresses = false;
        state.partnerAddress = null;
      })
      .addCase(
        getPartnerAddress.fulfilled,
        (state, action: PayloadAction<CompanyAddress>) => {
          state.loadingPartnerAddresses = false;
          state.partnerAddress = action.payload;
        }
      )
      // Company Customer Addresses
      .addCase(getCompanyCustomerAddresses.pending, (state) => {
        state.loadingCompanyAddresses = true;
      })
      .addCase(getCompanyCustomerAddresses.rejected, (state) => {
        state.loadingCompanyAddresses = false;
        state.companyCustomerAddresses = [];
      })
      .addCase(
        getCompanyCustomerAddresses.fulfilled,
        (state, action: PayloadAction<CompanyAddress[]>) => {
          state.loadingCompanyAddresses = false;
          state.companyCustomerAddresses = action.payload;
        }
      )
      // Company Addresses
      .addCase(getCompanyAddresses.pending, (state) => {
        state.loadingCompanyCustomerAddresses = true;
      })
      .addCase(getCompanyAddresses.rejected, (state) => {
        state.loadingCompanyCustomerAddresses = false;
        state.companyAddresses = [];
      })
      .addCase(
        getCompanyAddresses.fulfilled,
        (state, action: PayloadAction<CompanyAddress[]>) => {
          state.loadingCompanyCustomerAddresses = false;
          state.companyAddresses = action.payload;
        }
      )
      // Search Partners
      .addCase(searchPartners.rejected, (state) => {
        state.searchPartners = [];
      })
      .addCase(
        searchPartners.fulfilled,
        (state, action: PayloadAction<CompanyAddress[]>) => {
          state.searchPartners = action.payload;
        }
      )
      // Representatives
      .addCase(getPartnerRepresentatives.pending, (state) => {
        state.loadingRepresentatives = true;
      })
      .addCase(getPartnerRepresentatives.rejected, (state) => {
        state.loadingRepresentatives = false;
        state.representatives = [];
      })
      .addCase(
        getPartnerRepresentatives.fulfilled,
        (state, action: PayloadAction<PartnerRepresentative[]>) => {
          state.loadingRepresentatives = false;
          state.representatives = action.payload;
        }
      )
      // Customs Info
      .addCase(getPartnerCustomsInfo.pending, (state) => {
        state.loadingCustomsInfo = true;
      })
      .addCase(getPartnerCustomsInfo.rejected, (state) => {
        state.loadingCustomsInfo = false;
        state.customsInfo = null;
      })
      .addCase(
        getPartnerCustomsInfo.fulfilled,
        (state, action: PayloadAction<PartnerCustomsInfo>) => {
          state.loadingCustomsInfo = false;
          state.customsInfo = action.payload;
        }
      );
  },
});

export const partnerState = (state: RootState) => state.partnerSlice;

export default partnerSlice.reducer;